// Realitní kancelář – hledání v katalogu ([data-ro-search], markup includes/template-parts/redesign/reality-office/search-form.php).
// Formulář funguje i bez JS (GET na katalog, routes.php přesměruje typ/tr/obec na SEO cestu). JS přidává:
//  - živý popisek ceny (stejná logika jako efi_ro_price_range_label / efi_ro_mil v labels.php, texty vč. „mil.“
//    a „Kč“ z data-* – kolonky katalogu),
//  - od ≤ do (posouvaný jezdec se zastaví o druhý, jako onPmin/onPmax v návrhu),
//  - adresu formuláře = SEO cesta …/katalog/{tr}/{typ}/{obec}/ a odeslání bez prázdných parametrů,
//  - lokální našeptávač z JSON `.ro-suggest-data` (efi_ro_suggest_index): bez diakritiky, max 7, šipky/Enter/Escape;
//    výběr položky přejde rovnou na její adresu, text bez výběru se odešle jako `q`,
//  - s klíčem (data-mapy-key) místa z Mapy.com suggest (návrh „Napojení našeptávače Mapy.com“): odklad 250 ms,
//    zastaralé odpovědi se zahodí; k místům počty z lokálního indexu, za ně klíčová slova z indexu. Obec, kde
//    máme nabídky → SEO cesta (data.city), jiné místo → skrytá pole misto/lat/lng (catalog.php). Bez klíče,
//    při chybě nebo prázdné odpovědi platí lokální index,
//  - varianta `catalog` se po změně typu, transakce, ceny nebo počtu na stránce (select mimo formulář,
//    připojený atributem form=) odešle sama.

const NBSP = ' ';
const MAX_SUGGESTIONS = 7;
// celá ČR (locality=cz): obce vč. Prahy, části obcí a MČ, ulice, okresy a kraje – počet nabídek jen u míst z indexu
const MAPY_SUGGEST = 'https://api.mapy.com/v1/suggest';
const MAPY_TYPES = ['regional.municipality', 'regional.municipality_part', 'regional.street', 'regional.region'];
const MAPY_LIMIT = 6;
const MAPY_DELAY = 250;

const norm = (text) => String(text || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

const parseJson = (text, fallback) => {
  try {
    return JSON.parse(text || '') ?? fallback;
  } catch (e) {
    return fallback;
  }
};

// efi_ro_mil(): „4,5 mil. Kč“, 0 → „0 Kč“ (popisky z kolonek přes data-label-million / data-label-currency)
const milFormatter = (million, currency) => (amount) => {
  if (amount <= 0) {
    return `0${NBSP}${currency}`;
  }
  const value = (amount / 1000000).toFixed(2).replace(/\.?0+$/, '').replace('.', ',');
  return `${value}${NBSP}${million}${NBSP}${currency}`;
};

const countLabel = (n, forms) => {
  const form = n === 1 ? 'one' : (n >= 2 && n <= 4 ? 'few' : 'many');
  return `${n} ${forms[form] || ''}`.trim();
};

function initSearchForm(form) {
  const data = form.dataset;
  const max = parseInt(data.priceMax, 10) || 10000000;
  const slugs = parseJson(data.slugs, { typ: {}, tr: {} });
  const forms = parseJson(data.countForms, {});
  const ranges = { od: form.querySelector('[data-ro-range="od"]'), do: form.querySelector('[data-ro-range="do"]') };
  const priceLabel = form.querySelector('[data-ro-price-label]');
  const isCatalog = data.variant === 'catalog';
  const mil = milFormatter(data.labelMillion ?? 'mil.', data.labelCurrency ?? 'Kč');
  const perPage = form.elements.na_strance || null;

  form.classList.add('is-enhanced');

  const checked = (name) => {
    const input = form.querySelector(`input[name="${name}"]:checked`);
    return input ? input.value : '';
  };

  // …/katalog/{tr}/{typ}/{obec}/ – pořadí jako efi_ro_catalog_path()
  const path = () => {
    const parts = [];
    const tr = checked('tr');
    const typ = checked('typ');
    if (tr && slugs.tr[tr]) parts.push(slugs.tr[tr]);
    if (typ && slugs.typ[typ]) parts.push(slugs.typ[typ]);
    if (data.city) parts.push(data.city);
    return data.base + (parts.length ? `${parts.join('/')}/` : '');
  };

  const place = {
    misto: form.querySelector('[data-ro-place]'),
    lat: form.querySelector('[data-ro-lat]'),
    lng: form.querySelector('[data-ro-lng]'),
  };
  // název vybraného místa zůstává v poli – jako `q` se neposílá (jinak by se hledal i v textu nabídek)
  let placeLabel = place.misto && place.misto.value && form.elements.q ? form.elements.q.value.trim() : '';

  const setPlace = (misto, lat, lng, label) => {
    if (!place.misto) return;
    place.misto.value = misto;
    place.lat.value = lat;
    place.lng.value = lng;
    placeLabel = label;
  };

  const query = () => {
    const params = new URLSearchParams();
    const typed = form.elements.q ? form.elements.q.value.trim() : '';
    const q = placeLabel && typed === placeLabel ? '' : typed;
    if (!q && place.misto && place.misto.value) {
      params.set('misto', place.misto.value);
      if (place.lat.value && place.lng.value) {
        params.set('lat', place.lat.value);
        params.set('lng', place.lng.value);
      }
    }
    const from = ranges.od ? parseInt(ranges.od.value, 10) : 0;
    const to = ranges.do ? parseInt(ranges.do.value, 10) : max;
    if (q) params.set('q', q);
    if (from > 0) params.set('cena_od', String(from));
    if (to > 0 && to < max) params.set('cena_do', String(to));
    if (perPage && perPage.value && perPage.value !== data.perPageDefault) params.set('na_strance', perPage.value);
    return params;
  };

  const url = () => {
    const params = query().toString();
    return path() + (params ? `?${params}` : '');
  };

  // hero: „Hledat v katalogu — 44 nabídek“ – počet podle aktuálních filtrů z REST efi-ro/v1/pocet (routes.php)
  const submit = form.querySelector('[data-ro-submit]');
  let countTimer = 0;
  let countRequest = null;
  const updateCount = () => {
    if (!data.countUrl || !submit || !window.fetch) return;
    window.clearTimeout(countTimer);
    countTimer = window.setTimeout(() => {
      const params = query();
      const tr = checked('tr');
      const typ = checked('typ');
      if (tr) params.set('tr', tr);
      if (typ) params.set('typ', typ);
      if (data.city) params.set('obec', data.city);
      if (countRequest) countRequest.abort();
      countRequest = new AbortController();
      const endpoint = new URL(data.countUrl, window.location.href);
      params.forEach((value, key) => endpoint.searchParams.set(key, value));
      fetch(endpoint, { signal: countRequest.signal, headers: { Accept: 'application/json' } })
        .then((response) => (response.ok ? response.json() : null))
        .then((json) => {
          if (!json || typeof json.total !== 'number') return;
          submit.textContent = [data.button || '', countLabel(json.total, forms)].filter(Boolean).join(' — ');
        })
        .catch(() => {});
    }, 250);
  };

  const updatePrice = () => {
    if (!ranges.od || !ranges.do) return;
    const from = parseInt(ranges.od.value, 10) || 0;
    const to = parseInt(ranges.do.value, 10) || 0;
    const open = to <= 0 || to >= max;
    if (priceLabel) {
      priceLabel.textContent = from <= 0 && open
        ? data.labelAny || ''
        : `${mil(from)} – ${open ? data.labelNoLimit || '' : mil(to)}`;
    }
    ranges.od.setAttribute('aria-valuetext', mil(from));
    ranges.do.setAttribute('aria-valuetext', open ? data.labelNoLimit || mil(to) : mil(to));
  };

  Object.entries(ranges).forEach(([key, range]) => {
    if (!range) return;
    range.addEventListener('input', () => {
      const from = parseInt(ranges.od.value, 10);
      const to = parseInt(ranges.do.value, 10);
      if (from > to) {
        if (key === 'od') ranges.od.value = String(to);
        else ranges.do.value = String(from);
      }
      updatePrice();
      updateCount();
    });
    if (isCatalog) range.addEventListener('change', () => form.requestSubmit());
  });

  form.querySelectorAll('[data-ro-chip]').forEach((chip) => {
    chip.addEventListener('change', () => {
      form.action = path();
      if (isCatalog) form.requestSubmit();
      else updateCount();
    });
  });

  if (isCatalog && perPage && perPage.tagName === 'SELECT') {
    perPage.addEventListener('change', () => form.requestSubmit());
  }

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    window.location.assign(url());
  });

  // výběr místa z Mapy.com: obec s nabídkami → SEO cesta, jinak skrytá pole; ostatní filtry zůstanou
  const choosePlace = (item) => {
    if (form.elements.q) form.elements.q.value = item.label;
    if (item.city) {
      data.city = item.city;
      setPlace('', '', '', item.label); // název obce nese cesta, ne `q`
    } else {
      data.city = '';
      setPlace(item.misto, item.lat, item.lng, item.label);
    }
    window.location.assign(url());
  };

  // psaní do pole ruší vybrané místo (dál platí jen text)
  if (form.elements.q) {
    form.elements.q.addEventListener('input', () => {
      if (placeLabel && form.elements.q.value.trim() !== placeLabel) setPlace('', '', '', '');
      updateCount();
    });
  }

  form.action = path();
  updatePrice();
  initSuggest(form, forms, choosePlace);
}

// položka Mapy.com → { label, meta, misto, lat, lng, city?, count } (úrovně jako EFI_RO_PLACE_LEVELS v catalog.php)
function mapyPlace(item, cities, parts) {
  const structure = Array.isArray(item.regionalStructure) ? item.regionalStructure : [];
  const parent = (structure.find((s) => s.type === 'regional.municipality' && s.name !== item.name) || {}).name || '';
  const name = String(item.name || '');
  let level;
  let misto;
  if (item.type === 'regional.municipality') {
    level = 'obec';
    misto = `obec:${name}`;
  } else if (item.type === 'regional.region') {
    level = /^okres\s/i.test(name) ? 'okres' : 'kraj';
    misto = `${level}:${name}`;
  } else {
    if (!parent) return null;
    level = item.type === 'regional.street' ? 'ulice' : (/městská/i.test(item.label || '') ? 'mc' : 'cast');
    misto = `${level}:${parent}/${name}`;
  }
  const city = level === 'obec' ? cities.get(norm(name)) : null;
  // počet z indexu: obec podle názvu, část obce / MČ jako „Obec – Část“ (MČ „Brno-Líšeň“ i bez předpony obce)
  let count = city ? city.count : 0;
  if (level === 'cast' || level === 'mc') {
    const short = parent && norm(name).startsWith(`${norm(parent)}-`) ? name.slice(parent.length + 1) : name;
    count = parts.get(norm(`${parent} – ${name}`)) || parts.get(norm(`${parent} – ${short}`)) || 0;
  }
  const pos = item.position || {};
  return {
    label: name,
    kind: String(item.label || '').toLowerCase(),
    meta: item.location || '',
    misto,
    city: city ? city.city : '',
    lat: typeof pos.lat === 'number' ? pos.lat.toFixed(5) : '',
    lng: typeof pos.lon === 'number' ? pos.lon.toFixed(5) : '',
    count,
  };
}

function initSuggest(form, forms, choosePlace) {
  const input = form.querySelector('[data-ro-q]');
  const list = form.querySelector('[data-ro-suggest]');
  const source = form.querySelector('.ro-suggest-data');
  const key = form.dataset.mapyKey || '';
  if (!input || !list || (!source && !key)) return;

  const items = (source ? parseJson(source.textContent, []) : []).map((item) => ({ ...item, n: norm(item.label) }));
  const cities = new Map(items.filter((i) => i.city).map((i) => [i.n, i]));
  const parts = new Map(items.filter((i) => !i.city && i.n.includes(' – ')).map((i) => [i.n, i.count]));
  const placeKind = (items.find((i) => i.city) || {}).kind || '';
  let matches = [];
  let active = -1;
  let timer = 0;
  let seq = 0;

  const close = () => {
    list.hidden = true;
    list.innerHTML = '';
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
    matches = [];
    active = -1;
  };

  const setActive = (index) => {
    const options = list.querySelectorAll('[role="option"]');
    active = index;
    options.forEach((option, i) => {
      option.classList.toggle('is-active', i === index);
      option.setAttribute('aria-selected', i === index ? 'true' : 'false');
    });
    if (index >= 0 && options[index]) input.setAttribute('aria-activedescendant', options[index].id);
    else input.removeAttribute('aria-activedescendant');
  };

  // shoda jako suggest() v návrhu: začátek názvu, začátek slova, od 2 znaků kdekoli
  const localMatches = (n, onlyKeywords) => items
    .filter((item) => !onlyKeywords || (!item.n.includes(' – ') && !item.city))
    .filter((item) => item.n.startsWith(n) || item.n.includes(` ${n}`) || (n.length > 1 && item.n.includes(n)));

  const draw = (found) => {
    matches = found.slice(0, MAX_SUGGESTIONS);
    if (!matches.length) {
      close();
      return;
    }
    list.innerHTML = '';
    matches.forEach((item, i) => {
      const option = document.createElement('a');
      option.className = 'ro-suggest__option';
      option.id = `${list.id}-${i}`;
      option.href = item.url || '#';
      if (item.misto) {
        option.addEventListener('click', (event) => {
          event.preventDefault();
          choosePlace(item);
        });
      }
      option.tabIndex = -1;
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', 'false');
      const main = document.createElement('span');
      main.className = 'ro-suggest__main';
      const kind = document.createElement('span');
      kind.className = 'ro-suggest__kind';
      kind.textContent = item.kind || '';
      const label = document.createElement('span');
      label.textContent = item.label;
      main.append(kind, label);
      const meta = document.createElement('span');
      meta.className = 'ro-suggest__meta';
      // místo z Mapy.com: počet nabídek, jinak „okres Brno-město, Česko“
      meta.textContent = item.count ? countLabel(item.count, forms) : (item.meta || '');
      option.append(main, meta);
      list.append(option);
    });
    if (matches.some((item) => item.misto)) {
      const credit = document.createElement('div');
      credit.className = 'ro-suggest__credit';
      credit.setAttribute('aria-hidden', 'true');
      credit.textContent = '© Mapy.com';
      list.append(credit);
    }
    list.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    setActive(-1);
  };

  const fetchMapy = async (query, n, mySeq) => {
    const url = new URL(MAPY_SUGGEST);
    url.search = new URLSearchParams({ query, lang: 'cs', limit: String(MAPY_LIMIT), locality: 'cz', apikey: key });
    MAPY_TYPES.forEach((type) => url.searchParams.append('type', type));
    let places = [];
    try {
      const res = await fetch(url);
      if (res.ok) {
        const body = await res.json();
        places = (Array.isArray(body.items) ? body.items : []).map((i) => mapyPlace(i, cities, parts)).filter(Boolean);
      }
    } catch (e) {
      places = [];
    }
    if (mySeq !== seq || norm(input.value).trim() !== n) return; // zastaralá odpověď
    // „lokalita“ z kolonek, pokud Mapy.com druh nevrátí; bez míst z Mapy.com celý lokální index
    places.forEach((p) => { p.kind = p.kind || placeKind; });
    draw(places.length ? places.concat(localMatches(n, true)) : localMatches(n, false));
    // místa bez počtu v indexu (okres, kraj): počet přímo v místě z REST (obec bez nabídek zůstane bez čísla)
    const missing = places.filter((p) => !p.count);
    if (!missing.length || !form.dataset.placesCountUrl) return;
    try {
      const url = new URL(form.dataset.placesCountUrl, window.location.href);
      url.searchParams.set('mista', JSON.stringify(missing.map((p) => [p.misto])));
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      const body = res.ok ? await res.json() : null;
      if (!body || !Array.isArray(body.totals) || mySeq !== seq) return;
      missing.forEach((p, i) => { p.count = Number(body.totals[i]) || 0; });
      if (!missing.some((p) => p.count) || list.hidden) return;
      const was = active;
      draw(matches.slice());
      setActive(was); // překreslení nesmí shodit výběr šipkami
    } catch (e) {
      // bez počtů zůstane popis místa z Mapy.com
    }
  };

  const render = () => {
    const n = norm(input.value).trim();
    clearTimeout(timer);
    seq += 1;
    if (!n) {
      close();
      return;
    }
    if (!key || n.length < 2) {
      draw(localMatches(n, false));
      return;
    }
    const mySeq = seq;
    const query = input.value.trim();
    timer = setTimeout(() => fetchMapy(query, n, mySeq), MAPY_DELAY);
  };

  input.addEventListener('input', render);
  input.addEventListener('focus', render);
  input.addEventListener('blur', () => {
    clearTimeout(timer);
    seq += 1;
    close();
  });
  // klik do seznamu nesmí vzít fokus z pole (blur by seznam zavřel dřív, než odkaz zareaguje)
  list.addEventListener('mousedown', (event) => event.preventDefault());

  input.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      if (!list.hidden) {
        event.preventDefault();
        close();
      }
      return;
    }
    if (!matches.length) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      // -1 = zpět do pole (bez výběru), dokola přes všechny položky
      let next = active + (event.key === 'ArrowDown' ? 1 : -1);
      if (next >= matches.length) next = -1;
      if (next < -1) next = matches.length - 1;
      setActive(next);
    } else if (event.key === 'Enter' && active >= 0) {
      event.preventDefault();
      if (matches[active].misto) choosePlace(matches[active]);
      else window.location.assign(matches[active].url);
    }
  });
}

export default function initRoSearchForms() {
  document.querySelectorAll('[data-ro-search]').forEach(initSearchForm);
}
